import type { OverlayStorage, PersistedOverlayState } from '@packages/core/logging';

const STORAGE_KEY = 'subcomm:LOGGER_OVERLAY';
const OVERLAY_TTL_MS = 4 * 60 * 60 * 1000;

interface StoredOverlayEnvelope {
    expiresAt: number;
    state: PersistedOverlayState;
}

/**
 * sessionStorage persistence for the runtime overlay with a four hour expiry,
 * so a forgotten `rootLogger.reset()` self-heals. Storage failures (private
 * browsing, quota) degrade to "no persistence".
 */
export class SessionOverlayStorage implements OverlayStorage {
    constructor(private readonly storage: Storage) {}

    clear(): void {
        try {
            this.storage.removeItem(STORAGE_KEY);
        } catch {
            // Storage unavailable — nothing to clear.
        }
    }

    /** Load persisted state, discarding it when expired. */
    load(): null | PersistedOverlayState {
        try {
            const raw = this.storage.getItem(STORAGE_KEY);

            if (raw === null) {
                return null;
            }

            const envelope = JSON.parse(raw) as StoredOverlayEnvelope;

            if (Date.now() >= envelope.expiresAt) {
                this.storage.removeItem(STORAGE_KEY);
                return null;
            }

            return envelope.state;
        } catch {
            return null;
        }
    }

    save(state: PersistedOverlayState): void {
        try {
            const envelope: StoredOverlayEnvelope = {
                expiresAt: Date.now() + OVERLAY_TTL_MS,
                state,
            };
            this.storage.setItem(STORAGE_KEY, JSON.stringify(envelope));
        } catch {
            // Storage unavailable — overrides simply won't survive a reload.
        }
    }
}
